import React, { useState } from 'react';
import { Container, Card, Button, Table, Badge, Modal, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AssetForm from '../components/AssetForm';
import { useAssets, useCreateAsset, useUpdateAsset, useDeleteAsset } from '../hooks/useAssets';
import { useCommunity } from '../hooks/useCommunities';
import { useToast } from '../components/ToastNotification';
import { communityAssetTypes } from '../data/dummyData';
import { theme } from '../theme';
import { useAuth } from '../context/AuthContext';

/**
 * AssetsSetupPage - Manage community-level assets (solar farms, wind turbines, batteries).
 *
 * Uses useParams to get communityId from URL, then loads assets from the backend API.
 * For the /communities/new/assets route (no communityId), shows a warning that community
 * must be created first.
 *
 * Data upload is handled on a dedicated Data page, not here.
 */
const AssetsSetupPage = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { t } = useTranslation();
  const { id: communityId } = useParams();
  const { canEdit } = useAuth();

  // Fetch community detail for context (name, etc.)
  const { data: community } = useCommunity(communityId);

  // Fetch community-level assets
  const { data: assets = [], isLoading, isError, error } = useAssets(communityId);

  // Mutations
  const createMutation = useCreateAsset(communityId);
  const updateMutation = useUpdateAsset(communityId);
  const deleteMutation = useDeleteAsset(communityId);

  const [showModal, setShowModal] = useState(false);
  const [selectedAssetType, setSelectedAssetType] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);

  // -------------------------------------------------------------------
  // No communityId guard
  // -------------------------------------------------------------------
  if (!communityId) {
    return (
      <Container fluid className="py-4">
        <Alert variant="warning">
          <Alert.Heading>{t('assets_setup.no_community_title')}</Alert.Heading>
          <p>{t('assets_setup.no_community_text')}</p>
          <Button variant="primary" onClick={() => navigate('/communities/new')}>
            {t('assets_setup.create_community_btn')}
          </Button>
        </Alert>
      </Container>
    );
  }

  // -------------------------------------------------------------------
  // Asset CRUD handlers
  // -------------------------------------------------------------------
  const handleAddAsset = (type) => {
    setSelectedAssetType(type);
    setEditingAsset(null);
    setShowModal(true);
  };

  const handleEditAsset = (asset) => {
    // Determine asset type from the backend asset_type field
    setSelectedAssetType(asset.asset_type || asset.kind);
    setEditingAsset(asset);
    setShowModal(true);
  };

  const handleDeleteAsset = async (assetId) => {
    if (!window.confirm(t('assets_setup.delete_confirm'))) return;
    try {
      await deleteMutation.mutateAsync(assetId);
      addToast(t('assets_setup.delete_success'), 'success');
    } catch (err) {
      addToast(t('assets_setup.delete_failure', { error: err.message }), 'danger');
    }
  };

  const handleSubmitAsset = async (formData) => {
    // Map form data to CommunityAssetCreate schema:
    // { name, kind, asset_type, owner_scope, specs }
    const assetType = selectedAssetType;
    const payload = {
      name: formData.name || `${communityAssetTypes[assetType]?.label || assetType}`,
      kind: assetType, // legacy field
      asset_type: assetType,
      owner_scope: formData.ownerScope || 'community',
      specs: { ...formData.specs },
      metadata: {},
    };

    try {
      if (editingAsset) {
        await updateMutation.mutateAsync({
          assetId: editingAsset.id,
          data: {
            name: payload.name,
            specs: payload.specs,
            asset_type: payload.asset_type,
          },
        });
        addToast(t('assets_setup.update_success'), 'success');
      } else {
        await createMutation.mutateAsync(payload);
        addToast(t('assets_setup.create_success'), 'success');
      }
      setShowModal(false);
    } catch (err) {
      addToast(
        editingAsset ? t('assets_setup.update_failure', { error: err.message }) : t('assets_setup.create_failure', { error: err.message }),
        'danger'
      );
    }
  };

  // -------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------
  const getAssetsByType = (type) =>
    assets.filter(a => a.asset_type === type || a.kind === type);

  const getAssetIcon = (type) => communityAssetTypes[type]?.icon || '';
  const getAssetLabel = (type) => communityAssetTypes[type]?.label || type;
  const getAssetColor = (type) => communityAssetTypes[type]?.color || theme.colors.neutral[500];

  const isMutating = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  // -------------------------------------------------------------------
  // Loading / Error states
  // -------------------------------------------------------------------
  if (isLoading) {
    return (
      <Container fluid className="py-4 text-center">
        <Spinner animation="border" role="status" style={{ color: theme.colors.primary.main }}>
          <span className="visually-hidden">{t('common.loading')}</span>
        </Spinner>
        <p className="mt-3 text-muted">{t('assets_setup.loading_assets')}</p>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container fluid className="py-4">
        <Alert variant="danger">
          <Alert.Heading>{t('assets_setup.load_failed')}</Alert.Heading>
          <p>{error?.message || t('assets_setup.default_error')}</p>
          <Button variant="outline-danger" onClick={() => window.location.reload()}>
            {t('common.retry')}
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <div className="mb-4">
        <h2 className="mb-2">
          {t('assets_setup.header')}
        </h2>
        <p className="text-muted">
          {community?.name ? t('assets_setup.subtitle_named', { name: community.name }) : t('assets_setup.subtitle_default')}
        </p>
      </div>

      {/* Summary Cards */}
      <Row className="mb-4">
        {Object.entries(communityAssetTypes).map(([type, config]) => {
          const typeAssets = getAssetsByType(type);
          return (
            <Col key={type} md={4} className="mb-3">
              <Card
                className="text-center h-100 shadow-sm hover-shadow"
                style={{ cursor: canEdit ? 'pointer' : 'default', borderTop: `4px solid ${config.color}` }}
                onClick={canEdit ? () => handleAddAsset(type) : undefined}
              >
                <Card.Body>
                  <div style={{ fontSize: '2rem' }}>{config.icon}</div>
                  <div className="fw-bold mt-2">{typeAssets.length}</div>
                  <div className="small text-muted">{config.label}</div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Assets List */}
      {assets.length > 0 ? (
        <>
          {Object.keys(communityAssetTypes).map(type => {
            const typeAssets = getAssetsByType(type);
            if (typeAssets.length === 0) return null;

            return (
              <Card key={type} className="mb-4 shadow-sm">
                <Card.Header
                  style={{
                    backgroundColor: theme.colors.neutral[50],
                    borderLeft: `4px solid ${getAssetColor(type)}`,
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      <span className="me-2">{getAssetIcon(type)}</span>
                      {getAssetLabel(type)}
                    </h5>
                    {canEdit && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleAddAsset(type)}
                        disabled={isMutating}
                      >
                        <span className="me-1">+</span> {t('assets_setup.add_asset')}
                      </Button>
                    )}
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table hover responsive className="mb-0">
                    <thead style={{ backgroundColor: theme.colors.neutral[100] }}>
                      <tr>
                        <th>{t('assets_setup.col_name')}</th>
                        <th>{t('assets_setup.col_type')}</th>
                        <th>{t('assets_setup.col_specs')}</th>
                        <th className="text-end">{t('assets_setup.col_actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {typeAssets.map(asset => (
                        <tr key={asset.id}>
                          <td className="fw-semibold">{asset.name || asset.kind}</td>
                          <td>
                            <Badge bg={asset.owner_scope === 'community' ? 'success' : 'info'}>
                              {asset.owner_scope}
                            </Badge>
                          </td>
                          <td className="small">
                            {asset.specs && Object.entries(asset.specs).slice(0, 3).map(([key, value]) => (
                              <div key={key}>
                                <span className="text-muted">{key.replace(/_/g, ' ')}:</span> {value}
                              </div>
                            ))}
                          </td>
                          <td className="text-end">
                            {canEdit && (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="me-2"
                              onClick={() => handleEditAsset(asset)}
                              disabled={isMutating}
                            >
                              {t('assets_setup.btn_edit')}
                            </Button>
                            )}
                            {canEdit && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteAsset(asset.id)}
                              disabled={isMutating}
                            >
                              {t('assets_setup.btn_delete')}
                            </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            );
          })}
        </>
      ) : (
        <Alert variant="info" className="text-center py-5">
          <h4 className="mt-3">{t('assets_setup.no_assets_title')}</h4>
          <p className="mb-3">
            {t('assets_setup.no_assets_text')}
          </p>
          {canEdit && (
            <div className="d-flex gap-2 justify-content-center flex-wrap">
              <Button variant="outline-primary" onClick={() => handleAddAsset('solar_pv_farm')}>
                + {t('assets_setup.pv_assets')}
              </Button>
              <Button variant="outline-primary" onClick={() => handleAddAsset('community_battery')}>
                + {t('assets_setup.battery_assets')}
              </Button>
              <Button variant="outline-primary" onClick={() => handleAddAsset('wind_turbine')}>
                + {t('assets_setup.wind_assets')}
              </Button>
            </div>
          )}
        </Alert>
      )}

      {/* Asset Form Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingAsset ? t('assets_setup.modal_edit') : t('assets_setup.modal_add')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAssetType && (
            <AssetForm
              assetType={selectedAssetType}
              initialData={editingAsset}
              onSubmit={handleSubmitAsset}
              onCancel={() => setShowModal(false)}
            />
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default AssetsSetupPage;
